// Feedback Modal Component
// Modal form for collecting user feedback

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/api/base44Client';
import { toast } from '@/components/ui/use-toast';
import { CheckCircle, AlertCircle } from 'lucide-react';
import Icon from '@/components/ui/Icon';

const FeedbackModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  console.log('FeedbackModal rendered, isOpen:', isOpen); // Debug log
  const [formData, setFormData] = useState({
    feedback_type: 'general',
    title: '',
    description: '',
    user_email: '',
    page_url: ''
  });

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        feedback_type: 'general',
        title: '',
        description: '',
        user_email: user?.email || '',
        page_url: window.location.href
      });
      setIsSubmitted(false);
    }
  }, [isOpen, user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast({
        title: "Description required",
        description: "Please provide a description for your feedback.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Add timeout to prevent hanging forever (30 seconds)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out. Please check your connection and try again.')), 30000);
      });

      const result = await Promise.race([
        apiClient.Feedback.create(formData),
        timeoutPromise
      ]);
      
      // Only proceed if we got a successful result
      if (!result) {
        throw new Error('No data returned from feedback submission');
      }
      
      setIsSubmitted(true);
      
      toast({
        title: "Feedback submitted!",
        description: "Thank you for your feedback. We'll review it soon.",
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      
      // Ensure we're not in submitting state if there's an error
      setIsSubmitting(false);
      setIsSubmitted(false);
      
      // Provide more specific error messages
      let errorMessage = "There was an error submitting your feedback. Please try again.";
      
      if (error?.message?.includes('relation "feedback" does not exist')) {
        errorMessage = "Feedback system is not set up yet. Please contact support.";
      } else if (error?.message?.includes('permission denied') || error?.message?.includes('PGRST')) {
        errorMessage = "You don't have permission to submit feedback. Please log in.";
      } else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error submitting feedback",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      // Always reset submitting state
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const feedbackTypes = [
    { value: 'bug', label: '🐛 Bug Report', description: 'Something is broken or not working correctly' },
    { value: 'feature_request', label: '💡 Feature Request', description: 'Suggest a new feature or improvement' },
    { value: 'general', label: '💬 General Feedback', description: 'Share your thoughts or ask questions' }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="mb-6">
          <DialogTitle className="text-2xl font-bold text-center bg-gradient-to-r from-brand-600 to-brand-700 bg-clip-text text-transparent">
            Share Your Feedback
          </DialogTitle>
        </DialogHeader>

        {isSubmitted ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Thank You!
            </h3>
            <p className="text-gray-600 mb-4">
              Your feedback has been submitted successfully. We appreciate you taking the time to help us improve!
            </p>
            <div className="text-sm text-gray-500">
              This window will close automatically...
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Feedback Type */}
            <div className="space-y-2">
              <Label htmlFor="feedback_type" className="text-sm font-medium">
                What type of feedback is this?
              </Label>
              <Select 
                value={formData.feedback_type} 
                onValueChange={(value) => handleInputChange('feedback_type', value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select feedback type" />
                </SelectTrigger>
                <SelectContent>
                  {feedbackTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex flex-col">
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs text-gray-500">{type.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-medium">
                Brief Title (Optional)
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Login button not working"
                maxLength={100}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium">
                Description *
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Please describe your feedback in detail..."
                rows={6}
                className="resize-none"
                required
              />
              <div className="text-xs text-gray-500">
                {formData.description.length} characters
              </div>
            </div>

            {/* User Email */}
            <div className="space-y-2">
              <Label htmlFor="user_email" className="text-sm font-medium">
                Your Email (Optional)
              </Label>
              <Input
                id="user_email"
                type="email"
                value={formData.user_email}
                onChange={(e) => handleInputChange('user_email', e.target.value)}
                placeholder="your.email@example.com"
                disabled={!!user?.email}
              />
              {user?.email && (
                <div className="text-xs text-gray-500">
                  Using your account email: {user.email}
                </div>
              )}
            </div>

            {/* Current Page Info */}
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
              <div className="text-xs text-gray-600 dark:text-gray-400">
                <strong className="font-medium">Current page:</strong>
                <div className="mt-1 break-all text-gray-500 dark:text-gray-500">
                  {formData.page_url}
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !formData.description.trim()}
                className="flex-1 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="loader-circle" size="sm" className="mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Feedback
                  </>
                )}
              </Button>
            </div>

            {/* Help Text */}
            <div className="text-xs text-gray-500 text-center pt-2">
              Your feedback helps us improve Summer Squad. We read every submission!
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
